import { canViewModule } from "@/lib/permissions";
import { useAuthStore } from "@/stores/auth.store";
import { useUiStore } from "@/stores/ui.store";
import {
  Activity,
  Bell,
  Calendar,
  ChevronLeft,
  Flame,
  FolderOpen,
  LayoutDashboard,
  Lightbulb,
  Lock,
  Presentation,
  Rocket,
  Shield,
  Smartphone,
  Ticket,
  Users,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";

const allNavigationItems = [
  { label: "Tasks", icon: LayoutDashboard, path: "/tasks" },
  { label: "Calendar", icon: Calendar, path: "/calendar" },
  { label: "Team", icon: Users, path: "/team" },
  { label: "Monitoring", icon: Activity, path: "/monitoring" },
  { label: "Tickets", icon: Ticket, path: "/tickets" },
  { label: "Demos", icon: Presentation, path: "/demos" },
  { label: "Files", icon: FolderOpen, path: "/files" },
  { label: "APKs", icon: Smartphone, path: "/apks" },
  { label: "Firebase", icon: Flame, path: "/firebase" },
  { label: "Vault", icon: Lock, path: "/vault" },
  { label: "Ideas", icon: Lightbulb, path: "/ideas" },
  { label: "Apps", icon: Rocket, path: "/apps" },
  { label: "Push", icon: Bell, path: "/push" },
  { label: "Dev Tools", icon: Wrench, path: "/dev-tools" },
  { label: "Audit Logs", icon: Shield, path: "/audit-logs" },
];

export function Sidebar() {
  const {
    sidebarCollapsed: isCollapsed,
    toggleSidebar,
    mobileSidebarOpen,
    setMobileSidebarOpen,
  } = useUiStore();
  const location = useLocation();
  const { setCurrentModule } = useUiStore();
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role ?? "designer";

  const navigationItems = useMemo(
    () => allNavigationItems.filter((item) => canViewModule(userRole, item.label)),
    [userRole],
  );

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [setMobileSidebarOpen]);

  const handleNavClick = (label: string) => {
    setCurrentModule(label);
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-6 border-b border-zinc-800">
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-400" />
            <span className="text-lg font-bold">TUKI RAD</span>
          </div>
        )}
        {isCollapsed && <Zap className="w-6 h-6 text-amber-400 mx-auto" />}
        {/* Mobile close button */}
        <button
          onClick={() => setMobileSidebarOpen(false)}
          className="lg:hidden p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-800"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => handleNavClick(item.label)}
              className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                isActive
                  ? "bg-blue-600/10 text-blue-400 font-semibold border-l-2 border-blue-500 pl-2.5"
                  : "text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-200"
              }`}
              title={isCollapsed ? item.label : ""}
            >
              <Icon
                className={`w-5 h-5 flex-shrink-0 transition-transform group-hover:scale-110 ${isActive ? "text-blue-400" : "text-zinc-400"}`}
              />
              {!isCollapsed && <span className="text-sm tracking-tight">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse Button — desktop only */}
      <div className="hidden lg:block border-t border-zinc-800 p-3">
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors duration-200"
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          <ChevronLeft
            className={`w-5 h-5 transition-transform duration-300 ${
              isCollapsed ? "rotate-180" : ""
            }`}
          />
          {!isCollapsed && <span className="text-sm">Collapse</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex ${
          isCollapsed ? "w-20" : "w-64"
        } bg-zinc-900 text-white h-screen flex-col transition-all duration-300 ease-in-out border-r border-zinc-800`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile overlay sidebar */}
      {mobileSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
          {/* Drawer */}
          <aside className="fixed inset-y-0 left-0 w-64 bg-zinc-900 text-white flex flex-col z-50 lg:hidden shadow-xl">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}
